import React from "react";

export const createTelemetryEvent = ({
  feature,
  action,
  action_meta,
  ...additionalProps
}: {
  feature?: string;
  action?: string;
  action_meta?: string;
  [key: string]: TelemetryValue;
}) => ({
  fire: (channelName: string) => {
    // Build a telemetry event and dispatch it to the telemetry system
    const event = new BuildTelemetryEvent({ feature, action, action_meta, ...additionalProps });

    // Find the telemetry listener and trigger its onEvent handler
    const telemetryEvent = new CustomEvent("telemetry-event", {
      detail: { event, channel: channelName },
    });
    window.dispatchEvent(telemetryEvent);
  },
});

type TelemetryValue = string | number | boolean | null | undefined;

export type TelemetryEventPayload = {
  feature?: string;
  action?: string;
  [key: string]: TelemetryValue;
};

export class BuildTelemetryEvent {
  payload: Record<string, TelemetryValue>;
  context: Record<string, TelemetryValue>[];

  constructor(payload: Record<string, TelemetryValue>, context: Record<string, TelemetryValue>[] = []) {
    this.payload = payload;
    this.context = context;
  }
}

export const TelemetryListener = ({
  children,
  onEvent,
  channel,
}: {
  children: React.ReactNode;
  onEvent?: (event: BuildTelemetryEvent, channel?: string) => void;
  channel?: string;
}) => {
  React.useEffect(() => {
    const handleTelemetryEvent = (customEvent: CustomEvent) => {
      const { event, channel: eventChannel } = customEvent.detail;

      if (onEvent && (channel === eventChannel || !channel)) {
        onEvent(event, eventChannel);
      }
    };

    window.addEventListener("telemetry-event", handleTelemetryEvent as EventListener);

    return () => {
      window.removeEventListener("telemetry-event", handleTelemetryEvent as EventListener);
    };
  }, [onEvent, channel]);

  return children;
};
